import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

function RecentPostsSidebar({ type = 'blogs', limit = 5, excludeId = null }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentItems();
  }, [type, limit, excludeId]);

  const fetchRecentItems = async () => {
    setLoading(true);
    try {
      if (type === 'blogs') {
        const response = await axios.get('/api/blogs', {
          params: { page: 1, limit: limit + (excludeId ? 1 : 0) }
        });
        let blogs = response.data.blogs || [];
        // Filter out the current article if excludeId is provided
        if (excludeId) {
          blogs = blogs.filter(blog => blog.id !== excludeId).slice(0, limit);
        }
        setItems(blogs);
      } else if (type === 'events') {
        const response = await axios.get('/api/events/upcoming');
        let events = Array.isArray(response.data) ? response.data : [];
        // Filter out the current event if excludeId is provided
        if (excludeId) {
          events = events.filter(event => event.id !== excludeId);
        }
        setItems(events.slice(0, limit));
      } else if (type === 'projects') {
        const response = await axios.get('/api/projects', {
          params: { page: 1, limit: limit + (excludeId ? 1 : 0) }
        });
        let projects = response.data.projects || [];
        // Filter out the current project if excludeId is provided
        if (excludeId) {
          projects = projects.filter(project => project.id !== excludeId).slice(0, limit);
        }
        setItems(projects);
      }
    } catch (error) {
      console.error(`Error fetching recent ${type}:`, error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getImagePath = (image) => {
    if (!image) return '/img/Gallery/opd1.jpg';
    // Handle both cases: filename only or full path
    if (image.startsWith('/uploads/')) {
      return image;
    }
    return `/uploads/${image}`;
  };

  if (loading) {
    return (
      <div className="recent-posts-sidebar">
        <div className="text-center py-3">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  const getTitle = () => {
    if (type === 'blogs') return 'Recent Articles';
    if (type === 'events') return 'Upcoming Events';
    if (type === 'projects') return 'Other Projects';
    return 'Recent Items';
  };

  const getEmptyMessage = () => {
    if (type === 'blogs') return 'No articles available.';
    if (type === 'events') return 'No events available.';
    if (type === 'projects') return 'No other projects available.';
    return 'No items available.';
  };

  if (items.length === 0) {
    return (
      <div className="recent-posts-sidebar">
        <h5 className="sidebar-title mb-3" style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1a1a1a' }}>
          {getTitle()}
        </h5>
        <p className="text-muted">{getEmptyMessage()}</p>
      </div>
    );
  }

  return (
    <div className="recent-posts-sidebar">
      <h5 className="sidebar-title mb-3" style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1a1a1a' }}>
        {getTitle()}
      </h5>
      <div className="recent-posts-list">
        {items.map((item) => {
          const getProjectImage = (project) => {
            // Use featured image if available, otherwise use first gallery image
            if (project.featured_image) {
              return getImagePath(project.featured_image);
            }
            if (project.images && project.images.length > 0 && project.images[0].image) {
              return getImagePath(project.images[0].image);
            }
            return '/img/Gallery/opd1.jpg';
          };

          const getStatusBadge = (status) => {
            const badges = {
              ongoing: { text: 'Ongoing', class: 'bg-success' },
              completed: { text: 'Completed', class: 'bg-primary' },
              planned: { text: 'Planned', class: 'bg-warning' }
            };
            const badge = badges[status] || badges.planned;
            return <span className={`badge ${badge.class} text-white`} style={{ fontSize: '0.7rem' }}>{badge.text}</span>;
          };

          return (
            <div key={item.id} className="recent-post-item mb-3" style={{ borderBottom: '1px solid #e0e0e0', paddingBottom: '1rem' }}>
              {type === 'blogs' ? (
                <Link to={`/blog/${item.id}`} className="text-decoration-none" style={{ color: 'inherit' }}>
                  <div className="d-flex gap-3">
                    <div className="tgh-sidebar-thumb">
                      <img
                        src={getImagePath(item.image)}
                        alt={item.title}
                        onError={(e) => {
                          e.target.src = '/img/Gallery/opd1.jpg';
                        }}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h6 className="mb-1" style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1a1a1a', lineHeight: '1.3' }}>
                        {item.title}
                      </h6>
                      <p className="text-muted mb-1" style={{ fontSize: '0.75rem', margin: 0 }}>
                        {formatDate(item.created_at)}
                      </p>
                      {item.category && (
                        <span className="badge bg-secondary" style={{ fontSize: '0.7rem' }}>
                          {item.category}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ) : type === 'events' ? (
                <Link to={`/events/${item.id}`} className="text-decoration-none" style={{ color: 'inherit' }}>
                  <div className="d-flex gap-3">
                    <div className="tgh-sidebar-thumb">
                      <img
                        src={getImagePath(item.image)}
                        alt={item.title}
                        onError={(e) => {
                          e.target.src = '/img/Gallery/opd1.jpg';
                        }}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h6 className="mb-1" style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1a1a1a', lineHeight: '1.3' }}>
                        {item.title}
                      </h6>
                      <p className="text-muted mb-1" style={{ fontSize: '0.75rem', margin: 0 }}>
                        {formatDateTime(item.event_date)}
                      </p>
                      {item.location && (
                        <p className="text-muted mb-0" style={{ fontSize: '0.7rem', margin: 0 }}>
                          <i className="bi bi-geo-alt"></i> {item.location}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              ) : type === 'projects' ? (
                <Link to={`/projects/${item.id}`} className="text-decoration-none" style={{ color: 'inherit' }}>
                  <div className="d-flex gap-3">
                    <div className="tgh-sidebar-thumb">
                      <img
                        src={getProjectImage(item)}
                        alt={item.title}
                        onError={(e) => {
                          e.target.src = '/img/Gallery/opd1.jpg';
                        }}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h6 className="mb-1" style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1a1a1a', lineHeight: '1.3' }}>
                        {item.title}
                      </h6>
                      <p className="text-muted mb-1" style={{ fontSize: '0.75rem', margin: 0 }}>
                        {formatDate(item.start_date || item.created_at)}
                      </p>
                      {getStatusBadge(item.status)}
                    </div>
                  </div>
                </Link>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default RecentPostsSidebar;
